// Staged link bookkeeping shared by the Add/Edit Unit dialog and the
// Contacts tab's Manage Links / Edit record dialogs. Nothing here talks to
// HubSpot — it turns a dialog's `links` list into the payload the save
// endpoints expect (see server/lib/linkedRecords/linkedRecordsService.js).
//
// A link is { key, kind: "contact"|"company"|"entity", id, name, sub,
// origTypeIds, origTypeId, typeId, status: "saved"|"new"|"removed",
// existingId?, newFields? }.

export const KIND_CONFIG = {
	contact: { label: "Contact", plural: "Contacts" },
	company: { label: "Company", plural: "Companies" },
	entity: { label: "Entity", plural: "Entities" },
};

// "email · phone · mobile (mobile)" — same line the server builds for search results.
export const contactSub = (email, phone, mobile) => [email, phone, mobile && `${mobile} (mobile)`].filter(Boolean).join(" · ");

const subFor = (kind, r) => {
	if (kind === "contact") return contactSub(r.email, r.phone, r.mobilePhone);
	if (kind === "company") return [r.domain, r.phone].filter(Boolean).join(" · ");
	return [r.type, [r.address1, r.city, r.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
};

// Builds dialog links from the server's { contacts, companies, entities }
// shape. A record with several labels shows its first; changing it
// replaces them all.
export const linksFromRecords = (records) =>
	[
		...(records?.entities || []).map((r) => ({ kind: "entity", r })),
		...(records?.companies || []).map((r) => ({ kind: "company", r })),
		...(records?.contacts || []).map((r) => ({ kind: "contact", r })),
	].map(({ kind, r }) => {
		const origTypeIds = (r.roleTypeIds || []).map(String);
		return {
			key: `${kind}-${r.id}`,
			kind,
			id: String(r.id),
			name: r.name,
			sub: subFor(kind, r),
			origTypeIds,
			origTypeId: origTypeIds[0] || "",
			typeId: origTypeIds[0] || "",
			status: "saved",
		};
	});

export const stageAdd = (links, link) => [...links, { origTypeIds: [], origTypeId: "", status: "new", ...link }];

// New links are dropped outright; saved ones flip to/from "removed" (and
// lose any pending role change).
export const stageToggle = (links, key) =>
	links
		.filter((l) => !(l.key === key && l.status === "new"))
		.map((l) => (l.key === key ? { ...l, status: l.status === "removed" ? "saved" : "removed", typeId: l.origTypeId } : l));

export const stageRole = (links, key, typeId) => links.map((l) => (l.key === key ? { ...l, typeId } : l));

// After a save whose refreshed records didn't come back: treat everything
// staged as applied, so a second Save can't repeat the same creates/links.
export const settleLinks = (links) =>
	links
		.filter((l) => l.status !== "removed")
		.map((l) => ({ ...l, status: "saved", origTypeId: l.typeId, origTypeIds: l.typeId ? [l.typeId] : [] }));

// The save payload's adds/removes/roleChanges plus the footer's
// "2 to link, 1 to unlink, 1 role change" summary parts.
export const diffLinks = (links) => {
	const toLink = links.filter((l) => l.status === "new");
	const toUnlink = links.filter((l) => l.status === "removed");
	const relabeled = links.filter((l) => l.status === "saved" && l.typeId !== l.origTypeId);
	const parts = [];
	if (toLink.length) parts.push(`${toLink.length} to link`);
	if (toUnlink.length) parts.push(`${toUnlink.length} to unlink`);
	if (relabeled.length) parts.push(`${relabeled.length} role change${relabeled.length > 1 ? "s" : ""}`);
	return {
		parts,
		adds: toLink.map((l) => ({ kind: l.kind, existingId: l.existingId || null, newFields: l.newFields || null, typeId: l.typeId || null, name: l.name })),
		removes: toUnlink.map((l) => ({ kind: l.kind, id: l.id, name: l.name })),
		roleChanges: relabeled.map((l) => ({ kind: l.kind, id: l.id, fromTypeIds: l.origTypeIds, toTypeId: l.typeId || null, name: l.name })),
	};
};
